import { computed, type MaybeRefOrGetter, reactive, toValue, watch, type WatchSource } from 'vue'

import { reportError, reportOutcome } from '../lib/reportError'
import { createRetryLimiter, isRetryableError } from '../lib/retryLimit'

// Module-level, so the count survives closing and reopening a dialog. A page
// reload starts over; the backend is the real limit for costly generation.
const failures = reactive(new Map<string, number>())
const limiter = createRetryLimiter(failures)

type LimitOptions = {
  // Error of an automatic load that runs outside `run` (a query, a load on open).
  // Its first failure counts as the original attempt; its recovery resets the count.
  error?: WatchSource<unknown>
}

type RunOptions = {
  operation: string
  context?: Record<string, unknown>
}

/**
 * Tracks failures of one action (operation + entity id) and limits manual
 * retries to MAX_RETRY_ATTEMPTS. `run` reports each failure to Sentry, and the
 * failure that uses up the last retry as `<operation>-retry-exhausted`.
 */
export function useRetryLimit(key: MaybeRefOrGetter<string>, { error }: LimitOptions = {}) {
  let running = false

  const state = computed(() => {
    // Read through the reactive Map so the computed tracks this key.
    void failures.get(toValue(key))
    return limiter.state(toValue(key))
  })

  async function run<T>(fn: () => Promise<T>, { operation, context = {} }: RunOptions) {
    const currentKey = toValue(key)
    running = true
    try {
      const value = await fn()
      limiter.reset(currentKey)
      return value
    } catch (failure) {
      const next = limiter.recordFailure(currentKey)
      reportError(operation, failure, { ...context, attempt: next.failures })
      if (next.exhausted) {
        reportOutcome(`${operation}-retry-exhausted`, { ...context, attempt: next.failures })
      }
      throw failure
    } finally {
      running = false
    }
  }

  /** The original attempt failed outside `run`. The caller has reported it. */
  const noteFailure = () => limiter.recordOriginalFailure(toValue(key))
  const reset = () => limiter.reset(toValue(key))

  if (error) {
    watch(error, (current) => {
      if (current) noteFailure()
      // A load that works again on its own (a poll, a reopen) gives a fresh budget.
      else if (!running) reset()
    })
  }

  return {
    state,
    run,
    noteFailure,
    reset,
    isRetryableError,
  }
}
